import { ROUTE_CONFIG } from "@/routes/config";

/**
 * The Lead Scoring settings tabs, in display order (PREC-4055).
 *
 * The first six are numbered; the change log is a plain tab. "job-titles" only
 * shows when the JOB_TITLE_SCREENING feature is active, so a step's number is
 * computed over the visible steps, not this list.
 */
export const SCORE_STEPS = [
	"overview",
	"general",
	"company",
	"prospect",
	"scoring",
	"job-titles",
	"changelog",
] as const;

export type ScoreStep = (typeof SCORE_STEPS)[number];

const UNNUMBERED_STEPS: readonly ScoreStep[] = ["changelog"];

const JOB_TITLES_STEP: ScoreStep = "job-titles";

/** The route of one step. */
export const scoreStepPath = (step: ScoreStep): string => `${ROUTE_CONFIG.LEAD_SCORING}/${step}`;

/** The steps a user can see, given the job-title screening flag. */
export const visibleScoreSteps = (jobTitlesEnabled: boolean): ScoreStep[] =>
	SCORE_STEPS.filter((step) => step !== JOB_TITLES_STEP || jobTitlesEnabled);

/** The 1-based number shown on a step's badge, or null for an unnumbered step. */
export const scoreStepNumber = (step: ScoreStep, visible: ScoreStep[]): number | null => {
	if (UNNUMBERED_STEPS.includes(step)) {
		return null;
	}

	return visible.filter((candidate) => !UNNUMBERED_STEPS.includes(candidate)).indexOf(step) + 1;
};

/** Whether a URL segment names a step. */
export const isScoreStep = (value: string | undefined): value is ScoreStep =>
	!!value && (SCORE_STEPS as readonly string[]).includes(value);
